import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RULES = [
  ["len", "At least 10 characters", (p) => p.length >= 10],
  ["mix", "3 of: lowercase, UPPERCASE, number, symbol", (p) => [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(p)).length >= 3],
  ["uniq", "Not a common / leaked password", (p) => !/^(password|123456|qwerty|admin|welcome|letmein)/i.test(p)],
];

export function ChangePasswordForm({ forced = false, onDone }) {
  const { user, setUser } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = RULES.every(([, , t]) => t(pw)) && pw === pw2 && cur;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password changed");
      setUser({ ...user, must_change_password: false, password_age_days: 0, password_expires_in_days: 90 });
      setCur(""); setPw(""); setPw2("");
      onDone?.();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
      {forced && <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="change-password-forced-note"><span className="font-semibold">Set your own password to continue.</span> You signed in with a temporary password given by your admin. Choose a strong, unique one — browsers warn about passwords that appear in public data breaches.</div>}
      <label className="block"><span className="pp-label block mb-1">Current password</span><input type="password" autoComplete="current-password" className="pp-input w-full" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="cp-current" /></label>
      <label className="block"><span className="pp-label block mb-1">New password</span><input type="password" autoComplete="new-password" className="pp-input w-full" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="cp-new" /></label>
      <label className="block"><span className="pp-label block mb-1">Confirm new password</span><input type="password" autoComplete="new-password" className="pp-input w-full" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="cp-confirm" /></label>
      <ul className="text-xs space-y-1" data-testid="cp-rules">
        {RULES.map(([k, l, t]) => <li key={k} className={`flex items-center gap-1.5 ${t(pw) ? "text-emerald-700" : "text-slate-500"}`}><ShieldCheck size={12} /> {l}</li>)}
        <li className={`flex items-center gap-1.5 ${pw && pw === pw2 ? "text-emerald-700" : "text-slate-500"}`}><ShieldCheck size={12} /> Both entries match</li>
      </ul>
      <button type="submit" className="pp-btn-primary h-9 w-full" disabled={!ok || busy} data-testid="cp-submit"><KeyRound size={14} /> {busy ? "Saving…" : "Change password"}</button>
    </form>
  );
}

export function ForcePasswordChange() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="force-password-screen">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="text-xs font-bold tracking-widest text-blue-700 uppercase mb-1">PlantPulse</div>
        <h1 className="text-lg font-bold mb-3">Change your password</h1>
        <ChangePasswordForm forced />
      </div>
    </div>
  );
}
